import React, {useEffect} from 'react';
import {Form, Input, Modal, Select, message, Spin} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {SAVE_SORTER_PLAN} from '../graphql/mutations/saveSorterPlan';
import {GET_SORTER_PLAN, GetSorterPlanData, GetSorterPlanVariables} from '../graphql/queries/getSorterPlan';
import {useTranslation} from 'react-i18next';

const {Option} = Select;

interface SorterPlanModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    editingId?: string | null; // 改为只传 ID
    sorterOptions: Array<{value: string; label: string}>;
    warehouseOptions: Array<{value: string; label: string}>; // 作业仓下拉，只含 HUB（新增态用）
    allWarehouseOptions: Array<{value: string; label: string}>; // 全量仓（编辑态回显历史非 HUB 作业仓用，disabled 不可改）
    defaultWarehouseId?: number;
}

const SorterPlanModal: React.FC<SorterPlanModalProps> = ({
    visible,
    onCancel,
    onSuccess,
    editingId,
    sorterOptions,
    warehouseOptions,
    allWarehouseOptions,
    defaultWarehouseId
}) => {
    const [form] = Form.useForm();
    const {t} = useTranslation();
    const [saveSorterPlan, {loading: saving}] = useMutation(SAVE_SORTER_PLAN);

    // 当有 editingId 时，通过 GraphQL 查询获取方案详情
    const {data: planData, loading: loadingPlan} = useQuery<GetSorterPlanData, GetSorterPlanVariables>(
        GET_SORTER_PLAN,
        {
            variables: {id: editingId ? parseInt(editingId) : 0},
            skip: !editingId || !visible, // 只在编辑模式且弹窗可见时查询
            fetchPolicy: 'network-only', // 每次都从服务器获取最新数据
        }
    );

    useEffect(() => {
        if (visible) {
            if (editingId && planData?.getSorterPlan) {
                // 编辑模式 - 使用从 GraphQL 查询获取的最新数据
                const plan = planData.getSorterPlan;
                form.setFieldsValue({
                    sorterId: plan.sorterId ? Number(plan.sorterId) : undefined,
                    warehouseId: plan.warehouseId ? Number(plan.warehouseId) : undefined,
                    sorterPlanName: plan.sorterPlanName,
                    status: plan.status,
                });
            } else if (!editingId) {
                // 新增模式 - 设置默认值
                form.resetFields();
                form.setFieldsValue({
                    status: 0,
                    warehouseId: defaultWarehouseId,
                });
            }
        }
    }, [visible, editingId, planData, form, defaultWarehouseId]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const plan = planData?.getSorterPlan;
            await saveSorterPlan({
                variables: {
                    input: {
                        id: editingId ? parseInt(editingId) : null,
                        sorterId: parseInt(values.sorterId),
                        // 作业仓：新建必填；编辑时后端忽略该字段（创建后不可修改）
                        warehouseId: values.warehouseId ?? null,
                        sorterPlanName: values.sorterPlanName,
                        status: values.status,
                        // 保留后端字段，使用查询到的值或默认值
                        version: plan?.version || 0,
                        isDropBag: plan?.isDropBag || false,
                        isSpecified: plan?.isSpecified || false,
                        printCount: plan?.printCount ?? 1,
                    }
                }
            });

            message.success(
                editingId
                    ? t('wms.sorterPlan.messages.updateSuccess')
                    : t('wms.sorterPlan.messages.createSuccess')
            );
            form.resetFields();
            onSuccess();
        } catch (error: any) {
            console.error('Save sorter plan error:', error);
            
            // 提取错误消息
            let errorMessage = editingId
                ? t('wms.sorterPlan.messages.updateFailed')
                : t('wms.sorterPlan.messages.createFailed');
            
            // 优先检查 extensions.messageKey
            if (error?.graphQLErrors?.[0]?.extensions?.messageKey) {
                const messageKey = error.graphQLErrors[0].extensions.messageKey;
                errorMessage = t(messageKey) || messageKey;
            } else if (error?.graphQLErrors?.[0]?.message) {
                errorMessage = error.graphQLErrors[0].message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            message.error(errorMessage);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={editingId ? t('wms.sorterPlan.modal.editTitle') : t('wms.sorterPlan.modal.addTitle')}
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={saving}
            width={600}
        >
            <Spin spinning={loadingPlan}>
                <Form
                    form={form}
                    layout="horizontal"
                    labelAlign="left"
                    labelCol={{span: 6}}
                    wrapperCol={{span: 18}}
                >
                    <Form.Item
                        name="sorterId"
                        label={t('wms.sorterPlan.modal.sorterId')}
                        rules={[{required: true, message: t('wms.sorterPlan.validation.sorterIdRequired') as string}]}
                    >
                        <Select
                            placeholder={t('wms.sorterPlan.placeholders.selectSorter') as string}
                            showSearch
                            filterOption={(input, option) => {
                                const label = option?.label || '';
                                return String(label).toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {sorterOptions.map(option => (
                                <Option key={option.value} value={parseInt(option.value)}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="warehouseId"
                        label={t('wms.sorterPlan.modal.warehouse')}
                        rules={[{required: !editingId, message: t('wms.sorterPlan.validation.warehouseRequired') as string}]}
                    >
                        <Select
                            placeholder={t('wms.sorterPlan.placeholders.selectWarehouse') as string}
                            showSearch
                            optionFilterProp="children"
                            disabled={!!editingId}
                        >
                            {(editingId ? allWarehouseOptions : warehouseOptions).map(option => (
                                <Option key={option.value} value={parseInt(option.value)}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="sorterPlanName"
                        label={t('wms.sorterPlan.modal.sorterPlanName')}
                        rules={[{required: true, message: t('wms.sorterPlan.validation.sorterPlanNameRequired') as string}]}
                    >
                        <Input placeholder={t('wms.sorterPlan.placeholders.enterSorterPlanName') as string}/>
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label={t('wms.sorterPlan.modal.status')}
                        rules={[{required: true, message: t('wms.sorterPlan.validation.statusRequired') as string}]}
                    >
                        <Select
                            placeholder={t('wms.sorterPlan.placeholders.selectStatus') as string}
                            disabled={true}
                        >
                            <Option value={0}>{t('wms.sorterPlan.status.unused')}</Option>
                            <Option value={1}>{t('wms.sorterPlan.status.inUse')}</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};

export default SorterPlanModal;
